'use client';

/**
 * Floating dock for the property-wide calls centre.
 *
 * Positions `CallsCenter` on every PMS route so a call ringing an occupied
 * colleague is claimable from wherever the user is working — not only from the
 * dashboard.
 *
 * Bottom-LEFT is deliberate: toasts and the update banner own the bottom
 * centre/right, and the incoming/active call drawers slide in from the right
 * edge. Docking here keeps the pickup list readable while a drawer is open,
 * which is exactly the moment it matters — you are on one call and deciding
 * whether to claim the next.
 *
 * On mobile the panel is nearly full-width, so it starts COLLAPSED as a count
 * pill and expands on tap. A persistent panel covering half a phone screen
 * would be worse than the problem it solves. Desktop starts expanded.
 *
 * Renders nothing when nothing is ringing and the queue is empty (see
 * `CallsCenter`), so the dock costs no space on a quiet front desk.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useStaffCallStore } from '../hooks/useStaffCallClient';
import { CallsCenter } from './CallsCenter';
import { CallRegistryIcon } from './CallRegistryBadge';

interface CallsCenterFloatingProps {
  staffId: string;
}

export function CallsCenterFloating({ staffId }: CallsCenterFloatingProps) {
  const isMobile = useIsMobile();
  const callState = useStaffCallStore((s) => s.callState);
  const activeCall = useStaffCallStore((s) => s.activeCall);
  // `null` means "no explicit choice yet" — fall back to the breakpoint.
  //
  // Derived rather than synced through an effect: an effect that calls
  // setState on every breakpoint change costs an extra render pass, and this
  // needs no state at all. It also keeps the user's choice sticky across a
  // resize without a second flag to track whether they have toggled.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? !isMobile;

  // Same filter CallsCenter applies — the count must not include the call
  // already in this staff member's ear, or the pill claims "1 ringing" for a
  // call they are on and the expanded panel then shows an empty list.
  const ringingCount =
    callState?.ringing.filter((r) => r.callId !== activeCall?.callId).length ?? 0;
  const queueDepth = callState?.queueDepth ?? 0;

  if (ringingCount === 0 && queueDepth === 0) return null;

  return (
    <div
      id="calls_center_dock"
      className="fixed bottom-4 left-4 z-40 w-[min(22rem,calc(100vw-2rem))]"
    >
      {expanded ? (
        <div className="rounded-xl shadow-lg">
          <button
            type="button"
            onClick={() => setUserExpanded(false)}
            aria-label="Collapse calls centre"
            aria-expanded={true}
            className="flex items-center gap-1 w-full px-3 py-1.5 text-[11px] font-medium text-hms-textDim hover:text-hms-text transition-colors"
          >
            <ChevronDown className="w-3 h-3" />
            Hide
          </button>
          <div className="max-h-[60vh] overflow-y-auto">
            <CallsCenter staffId={staffId} />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setUserExpanded(true)}
          aria-label={`Show calls centre — ${ringingCount} ringing, ${queueDepth} queued`}
          aria-expanded={false}
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-hms-card border border-hms-border shadow-lg hover:bg-hms-surface transition-colors"
        >
          <CallRegistryIcon count={ringingCount} />
          <span className="text-xs font-medium text-hms-text">
            {ringingCount > 0
              ? `${ringingCount} ringing`
              : `${queueDepth} waiting`}
          </span>
        </button>
      )}
    </div>
  );
}
